import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { Search, Target, Sparkles, ArrowRight, Bookmark, ChevronRight, Home, ArrowLeft, GraduationCap, Briefcase, MapPin, TrendingUp, BookOpen, IndianRupee, ExternalLink, Rocket, Award, ClipboardList, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import SEOHead from '@/components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'IT & Software Development': { icon: '&#x1F4BB;', label: 'IT', accent: '#D95A41' },
  'Data Science & AI': { icon: '&#x1F916;', label: 'DS', accent: '#7C3AED' },
  'Government Jobs': { icon: '&#x1F3DB;', label: 'Gov', accent: '#D97706' },
  'Medical & Healthcare': { icon: '&#x2695;', label: 'Med', accent: '#DC2626' },
  'Commerce & Finance': { icon: '&#x1F4BC;', label: 'Fin', accent: '#059669' },
  'Creative & Media': { icon: '&#x1F3A8;', label: 'Art', accent: '#EC4899' },
  'Skilled Trades': { icon: '&#x1F527;', label: 'Trade', accent: '#EA580C' },
  'Core Engineering': { icon: '&#x2699;', label: 'Eng', accent: '#475569' },
  'Business & Entrepreneurship': { icon: '&#x1F680;', label: 'Biz', accent: '#0891B2' },
  'Study Abroad': { icon: '&#x2708;', label: 'Abroad', accent: '#2563EB' },
  'Digital Marketing': { icon: '&#x1F4F1;', label: 'DM', accent: '#0D9488' },
  'Law & Legal': { icon: '&#x2696;', label: 'Law', accent: '#7C2D12' },
};

const getCatStyle = (name) => CATEGORY_ICONS[name] || { icon: '&#x1F4DA;', label: '?', accent: '#6B7280' };

const SALARY_COLORS = ['#F4F1EA', '#E8A359', '#D95A41', '#2C5545', '#1C2B2D'];

const FAQPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [hierarchy, setHierarchy] = useState([]);
  const [totalFaqs, setTotalFaqs] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedEdu, setSelectedEdu] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [roadmap, setRoadmap] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    fetchHierarchy();
    if (user) fetchFavorites();
  }, [user]);

  const fetchHierarchy = async () => {
    try {
      const res = await axios.get(`${API}/faqs/hierarchy`);
      setHierarchy(res.data.hierarchy);
      setTotalFaqs(res.data.total_faqs);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites`);
      setFavorites(new Set(res.data.map(f => f.faq_id)));
    } catch (e) { console.error(e); }
  };

  const fetchFAQs = async (category, subcategory, topic) => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (category) p.set('category', category);
      if (subcategory) p.set('subcategory', subcategory);
      if (topic) p.set('topic', topic);
      const res = await axios.get(`${API}/faqs/browse?${p.toString()}`);
      setFaqs(res.data.faqs);
    } catch (e) { toast.error('Failed to load FAQs'); }
    finally { setLoading(false); }
  };

  const fetchRoadmap = async (role, education) => {
    try {
      const res = await axios.get(`${API}/roadmap?${new URLSearchParams({ role, education })}`);
      setRoadmap(res.data);
    } catch (e) { setRoadmap(null); }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/faqs?search=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data.faqs);
    } catch (e) { toast.error('Search failed'); }
    finally { setLoading(false); }
  };

  const clearSearch = () => { setSearchQuery(''); setSearchResults(null); };

  const selectCategory = (cat) => {
    setSelectedCategory(cat);
    setSelectedRole(null);
    setSelectedEdu(null);
    setRoadmap(null);
    setFaqs([]);
    setSearchResults(null);
  };

  const selectRole = (role) => {
    setSelectedRole(role);
    setSelectedEdu(null);
    setRoadmap(null);
    setFaqs([]);
  };

  const selectEdu = (edu) => {
    setSelectedEdu(edu);
    fetchFAQs(selectedCategory.name, selectedRole.name, edu.name);
    fetchRoadmap(selectedRole.name, edu.name);
  };

  const toggleFavorite = async (e, faqId) => {
    e.stopPropagation();
    if (!user) { toast.error('Please login to save favorites'); return; }
    try {
      if (favorites.has(faqId)) {
        await axios.delete(`${API}/favorites/${faqId}`);
        setFavorites(prev => { const n = new Set(prev); n.delete(faqId); return n; });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites/${faqId}`);
        setFavorites(prev => new Set(prev).add(faqId));
        toast.success('Saved to favorites!');
      }
    } catch (e) { toast.error('Failed to update favorites'); }
  };

  const parseSalaryToLPA = (range) => {
    if (!range) return 0;
    const s = range.replace(/[₹,]/g, '');
    const match = s.match(/([\d.]+)\s*-\s*([\d.]+)\s*LPA/i);
    if (match) return (parseFloat(match[1]) + parseFloat(match[2])) / 2;
    const single = s.match(/([\d.]+)\s*LPA/i);
    if (single) return parseFloat(single[1]);
    const monthMatch = s.match(/([\d.]+)\s*[KkLl].*month/i);
    if (monthMatch) {
      let val = parseFloat(monthMatch[1]);
      if (s.toLowerCase().includes('l')) val *= 100000;
      else if (s.toLowerCase().includes('k')) val *= 1000;
      return (val * 12) / 100000;
    }
    return 0;
  };

  const renderSalaryChart = () => {
    if (!roadmap?.salary) return null;
    const data = Object.entries(roadmap.salary)
      .filter(([, d]) => d.range !== 'N/A')
      .map(([level, d]) => ({ name: level.charAt(0).toUpperCase() + level.slice(1), range: d.range, value: parseSalaryToLPA(d.range), years: d.years }));
    if (data.length === 0) return null;
    return (
      <div data-testid="salary-chart" className="h-52 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barSize={40} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#596A6C' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#596A6C' }} axisLine={false} tickLine={false} unit=" L" />
            <Tooltip
              contentStyle={{ borderRadius: '12px', border: '1px solid #E5E0D8', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
              formatter={(value, name, props) => [props.payload.range, 'Salary']}
              labelFormatter={(label) => `${label} Level`}
            />
            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill={SALARY_COLORS[i] || '#D95A41'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderFAQAnswer = (answer) => (
    <div className="space-y-2.5">
      {answer.split('\n').map((line, i) => {
        line = line.trim();
        if (!line) return null;
        if (line.startsWith('**') && line.endsWith('**')) return <h3 key={i} className="text-base font-bold text-primary mt-5 mb-2 first:mt-0">{line.replace(/\*\*/g, '')}</h3>;
        if (/^\d+\./.test(line)) {
          const m = line.match(/^(\d+)\.\s*(.+)$/);
          if (m) return <div key={i} className="flex gap-3 ml-3 mb-1.5"><span className="font-bold text-primary flex-shrink-0">{m[1]}.</span><span className="text-earth-600">{m[2]}</span></div>;
        }
        if (line.includes('Learn:') || line.includes('Outcome:')) return <div key={i} className="ml-10 text-sm text-earth-500 mb-1.5">{line}</div>;
        if (line.startsWith('-') || line.startsWith('•')) return <div key={i} className="flex gap-2 ml-3 mb-1"><span className="text-primary">•</span><span className="text-earth-600">{line.substring(1).trim()}</span></div>;
        return <p key={i} className="text-earth-600 leading-relaxed">{line}</p>;
      })}
    </div>
  );

  // ===== FINDER PANELS =====

  const renderCategoryPanel = () => (
    <div className="finder-panel overflow-y-auto h-full">
      <div className="p-3 border-b border-earth-200 bg-surface-alt">
        <p className="text-xs uppercase tracking-[0.15em] font-bold text-earth-500">Categories</p>
      </div>
      {hierarchy.map((cat) => {
        const style = getCatStyle(cat.name);
        const isActive = selectedCategory?.name === cat.name;
        return (
          <button key={cat.name} data-testid={`cat-${cat.name.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => selectCategory(cat)}
            className={`w-full text-left px-3 py-3 border-b border-earth-100 flex items-center gap-3 transition-all text-sm ${isActive ? 'bg-surface-alt border-l-[3px] border-l-primary font-semibold' : 'hover:bg-earth-50'}`}>
            <span className="text-lg" dangerouslySetInnerHTML={{ __html: style.icon }}></span>
            <div className="flex-1 min-w-0">
              <div className={`truncate ${isActive ? 'text-earth-700' : 'text-earth-600'}`}>{cat.name}</div>
              <div className="text-xs text-earth-400">{cat.subcategories.length} roles</div>
            </div>
            <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-colors ${isActive ? 'text-primary' : 'text-earth-300'}`} />
          </button>
        );
      })}
    </div>
  );

  const renderRolePanel = () => {
    if (!selectedCategory) return (
      <div className="flex items-center justify-center h-full text-earth-400 text-sm p-6 text-center">
        <div><Briefcase className="w-8 h-8 mx-auto mb-2 opacity-40" /><p>Select a category to see career roles</p></div>
      </div>
    );
    return (
      <div className="finder-panel overflow-y-auto h-full">
        <div className="p-3 border-b border-earth-200 bg-surface-alt">
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-earth-500">Roles — {selectedCategory.name}</p>
        </div>
        {selectedCategory.subcategories.map((sub) => {
          const isActive = selectedRole?.name === sub.name;
          return (
            <button key={sub.name} data-testid={`sub-${sub.name.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => selectRole(sub)}
              className={`w-full text-left px-3 py-3 border-b border-earth-100 flex items-center gap-2 transition-all text-sm ${isActive ? 'bg-surface-alt border-l-[3px] border-l-primary font-semibold' : 'hover:bg-earth-50'}`}>
              <div className="flex-1 min-w-0">
                <div className={`truncate ${isActive ? 'text-earth-700' : 'text-earth-600'}`}>{sub.name}</div>
                <div className="text-xs text-earth-400">{sub.topics.length} edu paths • {sub.count} FAQs</div>
              </div>
              <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary' : 'text-earth-300'}`} />
            </button>
          );
        })}
      </div>
    );
  };

  const renderEduPanel = () => {
    if (!selectedRole) return (
      <div className="flex items-center justify-center h-full text-earth-400 text-sm p-6 text-center">
        <div><GraduationCap className="w-8 h-8 mx-auto mb-2 opacity-40" /><p>Select a role to see education paths</p></div>
      </div>
    );
    return (
      <div className="finder-panel overflow-y-auto h-full">
        <div className="p-3 border-b border-earth-200 bg-surface-alt">
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-earth-500">Education — {selectedRole.name}</p>
        </div>
        {selectedRole.topics.map((topic) => {
          const isActive = selectedEdu?.name === topic.name;
          return (
            <button key={topic.name} data-testid={`topic-${topic.name.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => selectEdu(topic)}
              className={`w-full text-left px-3 py-3 border-b border-earth-100 flex items-center gap-2 transition-all text-sm ${isActive ? 'bg-surface-alt border-l-[3px] border-l-primary font-semibold' : 'hover:bg-earth-50'}`}>
              <div className="flex-1">
                <div className={`${isActive ? 'text-earth-700' : 'text-earth-600'}`}>{topic.name}</div>
                <div className="text-xs text-earth-400">{topic.count} FAQ{topic.count > 1 ? 's' : ''}</div>
              </div>
              {isActive && <div className="w-2 h-2 rounded-full bg-primary"></div>}
            </button>
          );
        })}
      </div>
    );
  };

  // ===== ROADMAP + FAQ DETAIL PANEL =====
  const renderDetailPanel = () => {
    if (!selectedEdu) return (
      <div className="flex items-center justify-center h-full text-earth-400 text-sm p-8 text-center">
        <div><Target className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="text-base mb-1">Select a path to explore</p><p className="text-xs">Choose Category → Role → Education Level</p></div>
      </div>
    );

    return (
      <div className="overflow-y-auto h-full p-6 space-y-5 animate-fade-in-up">
        {/* Header */}
        <div className="bg-surface-alt rounded-2xl p-5 border border-earth-200">
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-primary mb-1">{selectedCategory?.name}</p>
          <h3 data-testid="faqs-heading" className="text-2xl font-bold text-earth-700">{selectedRole?.name}</h3>
          <p className="text-sm text-earth-500 mt-1">{selectedEdu?.name} • {selectedEdu?.count} FAQs</p>
        </div>

        {/* Roadmap */}
        {roadmap && (
          <div data-testid="career-roadmap" className="space-y-5">
            {/* Overview */}
            <div className="bg-white rounded-2xl border border-earth-200 p-6">
              <h3 className="text-lg font-bold text-earth-700 mb-2 flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" /> Role Overview
              </h3>
              <p className="text-earth-600 leading-relaxed text-sm">{roadmap.overview}</p>
            </div>

            {/* Work Duties */}
            {roadmap.work_duties?.length > 0 && (
              <div data-testid="work-duties-section" className="bg-white rounded-2xl border border-earth-200 p-6">
                <h3 className="text-lg font-bold text-earth-700 mb-3 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-secondary" /> Work Duties
                </h3>
                <ul className="space-y-2">
                  {roadmap.work_duties.map((duty, i) => (
                    <li key={i} className="flex gap-3 text-sm text-earth-600">
                      <span className="text-primary font-bold mt-0.5 flex-shrink-0">{i + 1}.</span>
                      <span>{duty}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Responsibilities */}
            <div className="bg-white rounded-2xl border border-earth-200 p-6">
              <h3 className="text-lg font-bold text-earth-700 mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-accent" /> Key Responsibilities
              </h3>
              <ul className="space-y-2">
                {roadmap.responsibilities.map((r, i) => (
                  <li key={i} className="flex gap-3 text-sm text-earth-600">
                    <span className="text-accent font-bold mt-0.5 flex-shrink-0">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Salary Chart + Data */}
            <div className="bg-white rounded-2xl border border-earth-200 p-6">
              <h3 className="text-lg font-bold text-earth-700 mb-2 flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-success" /> Salary Roadmap
              </h3>
              {renderSalaryChart()}
              <div className="space-y-2 mt-4">
                {Object.entries(roadmap.salary).map(([level, data]) => (
                  data.range !== 'N/A' && (
                    <div key={level} className="flex items-center gap-3 p-2.5 rounded-lg bg-earth-50 border border-earth-100 text-sm">
                      <span className="text-xs font-bold uppercase text-earth-500 bg-earth-100 px-2 py-0.5 rounded w-20 text-center flex-shrink-0">{level}</span>
                      <span className="font-bold text-earth-700 flex-1">{data.range}</span>
                      <span className="text-xs text-earth-400 flex-shrink-0">{data.years}</span>
                    </div>
                  )
                ))}
              </div>
              {roadmap.salary_sources?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-earth-100">
                  <p className="text-xs text-earth-400 flex items-center gap-1 flex-wrap">
                    <ExternalLink className="w-3 h-3" /> Sources:
                    {roadmap.salary_sources.map((s, i) => (
                      <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                        {s.name}{i < roadmap.salary_sources.length - 1 ? ',' : ''}
                      </a>
                    ))}
                  </p>
                </div>
              )}
            </div>

            {/* Demanded Areas */}
            <div className="bg-white rounded-2xl border border-earth-200 p-6">
              <h3 className="text-lg font-bold text-earth-700 mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" /> Top Demanded Areas (AP/TG)
              </h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {roadmap.demanded_areas.map((area, i) => (
                  <div key={i} className="p-3 rounded-xl bg-earth-50 border border-earth-100">
                    <span className="font-bold text-earth-700 text-sm">{area.location}</span>
                    <p className="text-xs text-earth-500 mt-0.5">{area.details}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Learning Path */}
            {roadmap.learning_path && (
              <div className="bg-white rounded-2xl border-2 border-primary/20 p-6">
                <h3 className="text-lg font-bold text-earth-700 mb-1 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" /> Your Learning Path
                </h3>
                <p className="text-sm text-primary font-medium">{roadmap.learning_path.label}</p>
                <p className="text-xs text-earth-500 mb-4">Total: <strong>{roadmap.learning_path.total_duration}</strong></p>
                <div className="relative">
                  <div className="absolute left-[18px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary to-warning"></div>
                  <div className="space-y-3">
                    {roadmap.learning_path.steps.map((step, i) => (
                      <div key={i} className="flex gap-3 relative">
                        <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold flex-shrink-0 z-10 shadow-sm">
                          {step.step}
                        </div>
                        <div className="flex-1 bg-earth-50 rounded-xl p-3 border border-earth-100">
                          <div className="flex items-center justify-between mb-0.5 flex-wrap gap-1">
                            <h5 className="font-bold text-earth-700 text-sm">{step.title}</h5>
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{step.duration}</span>
                          </div>
                          <p className="text-xs text-earth-500">{step.details}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Career Growth */}
            <div className="bg-white rounded-2xl border border-earth-200 p-6">
              <h3 className="text-lg font-bold text-earth-700 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-secondary" /> Career Growth
              </h3>
              <div className="space-y-2">
                {roadmap.career_growth.map((path, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-earth-50 border border-earth-100">
                    <Rocket className="w-4 h-4 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-bold text-earth-700 text-sm">{path.role}</span>
                      <span className="text-xs text-earth-400 ml-2">({path.years})</span>
                      <p className="text-xs text-earth-500">{path.description}</p>
                    </div>
                    <span className="text-xs font-bold text-primary flex-shrink-0">{path.salary}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* More Opportunities */}
            <div className="bg-white rounded-2xl border border-earth-200 p-6">
              <h3 className="text-lg font-bold text-earth-700 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-warning" /> More Opportunities
              </h3>
              <ul className="space-y-2">
                {roadmap.opportunities.map((opp, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-earth-600">
                    <span className="text-warning font-bold mt-0.5 flex-shrink-0">→</span>
                    <span>{opp}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* FAQ List */}
        {faqs.length > 0 && (
          <div className="mt-6">
            <h4 data-testid="faq-list-heading" className="text-lg font-bold text-earth-700 mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-primary" /> Frequently Asked Questions ({faqs.length})
            </h4>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <div key={faq.id} data-testid={`faq-item-${idx}`}
                  className={`bg-white rounded-xl border overflow-hidden transition-all duration-300 ${expandedFAQ === faq.id ? 'border-primary shadow-md' : 'border-earth-200 hover:border-earth-300'}`}>
                  <div className="flex items-center w-full p-4 hover:bg-earth-50/50 transition-colors">
                    <button onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)} className="flex-1 text-left">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs bg-surface-alt text-earth-600 px-2 py-0.5 rounded-full font-semibold border border-earth-200">{faq.category}</span>
                        {faq.metadata?.location && <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full font-medium">{faq.metadata.location}</span>}
                        <span className="text-xs text-earth-400">• {faq.popularity_score}% helpful</span>
                      </div>
                      <h4 className="text-sm font-semibold text-earth-700 leading-snug">{faq.question}</h4>
                    </button>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                      <button data-testid={`faq-favorite-${idx}`} onClick={(e) => toggleFavorite(e, faq.id)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${favorites.has(faq.id) ? 'bg-warning/10 text-warning hover:bg-warning/20' : 'bg-earth-100 text-earth-400 hover:text-warning'}`}>
                        <Bookmark className={`w-4 h-4 ${favorites.has(faq.id) ? 'fill-warning' : ''}`} />
                      </button>
                      <button onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                        className={`w-7 h-7 rounded-full bg-earth-100 flex items-center justify-center transition-all duration-300 ${expandedFAQ === faq.id ? 'rotate-180 text-primary' : 'text-earth-400'}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                    </div>
                  </div>
                  {expandedFAQ === faq.id && (
                    <div className="px-4 pb-4 bg-earth-50 border-t border-earth-100">
                      <div className="pt-4">{renderFAQAnswer(faq.answer)}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}
      </div>
    );
  };

  // ===== SEARCH RESULTS =====
  const renderSearchOverlay = () => {
    if (!searchResults) return null;
    return (
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-earth-700">Results for "{searchQuery}"</h3>
            <p className="text-sm text-earth-500">{searchResults.length} results found</p>
          </div>
          <Button onClick={clearSearch} variant="ghost" size="sm" className="text-earth-500 hover:text-primary">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        </div>
        {searchResults.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-earth-200">
            <Search className="w-12 h-12 text-earth-300 mx-auto mb-3" />
            <p className="text-earth-500">No FAQs found. Try different keywords.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {searchResults.map((faq, idx) => (
              <div key={faq.id} data-testid={`search-result-${idx}`}
                className={`bg-white rounded-xl border overflow-hidden transition-all duration-300 ${expandedFAQ === faq.id ? 'border-primary shadow-md' : 'border-earth-200 hover:border-earth-300'}`}>
                <div className="flex items-center w-full p-4 hover:bg-earth-50/50 transition-colors">
                  <button onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)} className="flex-1 text-left">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs bg-surface-alt text-earth-600 px-2 py-0.5 rounded-full font-semibold border border-earth-200">{faq.category}</span>
                      {faq.metadata?.location && <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full font-medium">{faq.metadata.location}</span>}
                    </div>
                    <h4 className="text-sm font-semibold text-earth-700 leading-snug">{faq.question}</h4>
                  </button>
                  <button onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                    className={`w-7 h-7 rounded-full bg-earth-100 flex items-center justify-center transition-all duration-300 ml-3 ${expandedFAQ === faq.id ? 'rotate-180 text-primary' : 'text-earth-400'}`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                </div>
                {expandedFAQ === faq.id && (
                  <div className="px-4 pb-4 bg-earth-50 border-t border-earth-100">
                    <div className="pt-4">{renderFAQAnswer(faq.answer)}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead title="Career FAQs — O4S.ai" description="Browse 1890+ career FAQs for Indian students. Explore salary data, career roadmaps, and expert guidance for AP & Telangana." path="/faqs" />
      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 lg:px-12 py-4 border-b border-earth-200">
        <div className="max-w-[1600px] mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-earth-700">O4S.ai</h1>
          </div>
          <div className="flex gap-3">
            {user ? (
              <Button data-testid="goto-dashboard-btn" onClick={() => navigate('/dashboard')} variant="outline" className="border-earth-200 text-earth-600 hover:border-primary hover:text-primary rounded-xl btn-press">Dashboard</Button>
            ) : (
              <Button data-testid="goto-login-btn" onClick={() => navigate('/auth')} className="bg-primary hover:bg-primary-hover text-white rounded-xl btn-press">Get Started</Button>
            )}
          </div>
        </div>
      </nav>

      {/* Search Bar */}
      <div className="px-4 md:px-8 lg:px-12 py-5 bg-secondary">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center gap-4 mb-3">
            <div>
              <h2 className="text-xl font-bold text-white">Career Knowledge Base</h2>
              <p className="text-sm text-white/70">{totalFaqs.toLocaleString()}+ expert answers across {hierarchy.length} industries</p>
            </div>
            <Button data-testid="start-ai-assessment-btn" onClick={() => navigate(user ? '/assessment' : '/auth')}
              className="ml-auto bg-white/15 hover:bg-white/25 text-white h-9 px-5 text-sm rounded-xl border border-white/20 btn-press hidden md:flex">
              <Sparkles className="w-4 h-4 mr-1.5" /> AI Assessment
            </Button>
          </div>
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-earth-400 w-5 h-5" />
            <Input data-testid="faq-search-input" type="text"
              placeholder="Search career questions... (e.g., 'data scientist salary Hyderabad')"
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base bg-white text-earth-700 border-0 rounded-xl" />
            <Button data-testid="search-submit-btn" type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-primary hover:bg-primary-hover text-white h-9 px-5 rounded-lg text-sm btn-press">Search</Button>
          </form>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-4 md:px-8 lg:px-12 py-6">
        <div className="max-w-[1600px] mx-auto">
          {searchResults ? renderSearchOverlay() : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 bg-white rounded-2xl border border-earth-200 overflow-hidden shadow-sm" style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}>
              {/* Column 1: Categories */}
              <div className="lg:col-span-3 border-r border-earth-200 overflow-hidden">
                {renderCategoryPanel()}
              </div>
              {/* Column 2: Roles */}
              <div className="lg:col-span-3 border-r border-earth-200 overflow-hidden">
                {renderRolePanel()}
              </div>
              {/* Column 3: Education + Detail */}
              <div className="lg:col-span-6 overflow-hidden flex flex-col">
                {selectedEdu ? (
                  renderDetailPanel()
                ) : selectedRole ? (
                  renderEduPanel()
                ) : (
                  <div className="flex items-center justify-center h-full text-earth-400 text-sm p-8 text-center">
                    <div>
                      <Target className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-base mb-1 font-medium">Explore Career Paths</p>
                      <p className="text-xs max-w-xs mx-auto">Navigate through categories and roles to discover detailed career roadmaps with salary data, learning paths, and expert FAQs</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CTA */}
          <section className="mt-10 bg-secondary rounded-2xl p-10 text-white text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-80" />
            <h3 className="text-2xl font-bold mb-2">Need Personalized Career Guidance?</h3>
            <p className="text-base text-white/80 mb-5 max-w-xl mx-auto">Get AI-powered analysis with 5-6 personalized career paths tailored to your education, skills, and goals</p>
            <Button data-testid="cta-ai-assessment-btn" onClick={() => navigate(user ? '/assessment' : '/auth')}
              className="bg-white text-secondary hover:bg-earth-100 h-12 px-8 text-base font-bold rounded-xl btn-press transition-colors">
              Start AI Assessment <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
